import { nextTick, useNuxtApp, useState } from "#imports";
import { useChatSessionTimeout } from "./useChatSessionTimeout.js";
import { createWelcomeMessage } from "../utils/chatUtils.js";
export function useChat() {
  const { $chat } = useNuxtApp();
  const isSending = useState("isSending", () => false);
  const isConnected = useState("isConnected", () => false);
  const isChatEnded = useState("isChatEnded", () => false);
  const messages = useState("messages", () => []);
  const channelType = useState("channelType", () => "");
  const channelId = useState("channelId", () => "");
  const sessionTimeout = useChatSessionTimeout();
  sessionTimeout.configureSessionTimeout({
    onSessionWarning,
    onSessionExpired
  });
  const connectWithProvider = async (tokenProvider, config, options) => {
    try {
      if (options?.welcomeMessage) {
        messages.value = [createWelcomeMessage(options.welcomeMessage)];
      }
      const { user, token, name, channelType: userChannelType, channelId: userChannelId } = await tokenProvider();
      channelType.value = userChannelType;
      channelId.value = userChannelId;
      await $chat.connect(user, token, name, config);
      await $chat.setConversation(userChannelType, userChannelId);
      const streamMessages = $chat.getMessages();
      messages.value = [...messages.value, ...streamMessages];
      $chat.onMessageReceived(receiveMessage);
      isConnected.value = true;
      sessionTimeout.startSession();
    } catch (error) {
      console.error("Chat connection failed:", error);
      isConnected.value = false;
      throw error;
    }
  };
  function receiveMessage(message) {
    if (!message) return;
    const index = messages.value.findIndex((m) => m.id === message.id);
    if (index !== -1) {
      messages.value[index] = message;
    } else {
      messages.value.push(message);
    }
    if (message.user?.id.includes("ai_agent")) {
      isSending.value = false;
      const chatInput = document.querySelector(`#ai-chat-input-${channelId.value}`);
      nextTick(() => {
        chatInput?.focus?.();
      });
    }
    sessionTimeout.resetSessionTimeout();
  }
  async function sendMessage(text, customData = {}) {
    if (!text.trim()) {
      throw new Error("Message cannot be empty");
    }
    if (!isConnected.value) {
      throw new Error("Chat is not connected, sendMessage failed");
    }
    isSending.value = true;
    try {
      await $chat.sendMessage(text, customData);
      sessionTimeout.resetSessionTimeout();
    } catch (error) {
      console.error("Error sending message:", error);
      isSending.value = false;
      throw error;
    }
  }
  async function disconnect() {
    if (isConnected.value) {
      try {
        await $chat.disconnect();
        isConnected.value = false;
        const welcomeMessage = messages.value.find((msg) => msg.id === "welcome-message");
        messages.value = welcomeMessage ? [welcomeMessage] : [];
        channelType.value = "";
        channelId.value = "";
        sessionTimeout.endSession();
      } catch (error) {
        console.error("Error during disconnect:", error);
      }
    }
  }
  function onSessionWarning() {
  }
  function onSessionExpired() {
    isChatEnded.value = true;
  }
  return {
    messages,
    isConnected,
    isChatEnded,
    isSending,
    channelType,
    channelId,
    connectWithProvider,
    sendMessage,
    disconnect
  };
}
